// src/components/PINProtectedRoute.jsx
import React, { useEffect, useState } from "react";
import { Route } from "react-router-dom";
import PINPrompt from "./PINPrompt";
import { isPinSessionValid, touchPinSession } from "../utils/PINSession";
import { checkHasPin } from "../utils/PINService";
import PINCreate from "../pages/PINCreate";

export default function ProtectedRouteWithPin({ component, children, path, exact, ...rest }) {
  const [errorMsg, setErrorMsg] = useState("");
  const [state, setState] = useState({ status: "checking" }); // checking | needCreate | needPrompt | ok
  // support both component prop as reference or element:
  const Component = component;

  useEffect(() => {
    let mounted = true;
    async function chk() {
      if (isPinSessionValid()) {
        if (!mounted) return;
        setState({ status: "ok" });
        return;
      }
      try {
        const exists = await checkHasPin();
        if (!mounted) return;
        if (!exists) setState({ status: "needCreate" });
        else setState({ status: "needPrompt" });
      } catch (err) {
        // jika error saat cek, treat as needPrompt (safer)
        if (!mounted) return;
        setState({ status: "needPrompt" });
      }
    }
    chk();
    return () => { mounted = false; };
  }, [path]);

  // touch session on activity
  useEffect(() => {
    function onActivity() {
      touchPinSession();
    }
    window.addEventListener("mousemove", onActivity);
    window.addEventListener("keydown", onActivity);
    window.addEventListener("touchstart", onActivity);
    return () => {
      window.removeEventListener("mousemove", onActivity);
      window.removeEventListener("keydown", onActivity);
      window.removeEventListener("touchstart", onActivity);
    };
  }, []);

  if (state.status === "checking") {
    return <div style={{ padding: "1rem" }}>Memeriksa hak akses...</div>;
  }

  if (state.status === "needCreate") {
    return <PINCreate />;
  }

  if (state.status === "needPrompt") {
    return (
      <PINPrompt
        onSuccess={() => setState({ status: "ok" })}
        onCancel={() => window.location.replace("/")}
      />
    );
  }

  // state.status === 'ok' -> render target component
  // Accept multiple ways caller passed target:
  // - component={UmatData} (reference)
  // - component={<UmatData someProp={x} />} (element)
  // - children={<UmatData />} (element via children)
  return (
    <Route
      path={path}
      exact={exact}
      render={(props) => {
        // prefer children if provided
        if (children) return React.isValidElement(children) ? children : children(props);
        if (Component) {
          // if component is a React element already, render it directly
          if (React.isValidElement(Component)) return Component;
          // if component is a function/class, render as element
          if (typeof Component === "function") {
            const Comp = Component;
            return <Comp {...props} {...rest} />;
          }
        }
        // fallback: nothing
        return null;
      }}
    />
  );
}
